import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { PublicMedia } from '../types';
import { PublicApi } from '../utils/apiClient';

const EMPTY: PublicMedia = { single: {}, galleries: {}, customers: [] };

interface MediaCtx {
  media: PublicMedia;
  ready: boolean;
  /** absolute url for a SINGLE slot, or '' when unavailable */
  single: (slot: string) => string;
  gallery: (group: string) => string[];
}

const Ctx = createContext<MediaCtx | null>(null);

export const MediaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [media, setMedia] = useState<PublicMedia>(EMPTY);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    PublicApi.media()
      .then((m) => { if (!cancelled && m) setMedia({ ...EMPTY, ...m }); })
      .catch(() => { /* graceful degradation: CSS fallbacks */ })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo<MediaCtx>(() => ({
    media,
    ready,
    single: (slot: string) => media.single?.[slot] || '',
    gallery: (group: string) => media.galleries?.[group] || [],
  }), [media, ready]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useMedia(): MediaCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMedia must be used within MediaProvider');
  return ctx;
}
